import { FC, useCallback } from 'react';
import { Col, Row } from 'reactstrap';

import { useForm, useFormState } from '@/utils/hooks';
import { CompanySettings } from '@/types/company-settings';
import config, {
  mga_inventory_depletion_method,
  mga_disable_warnings_on_reviewed_period,
  MGASettingsType,
  MGAInventoryDepletionMethod
} from 'views/companies/settings/mga/config';
import { FieldsStateType } from '@/common/types/form';
import { getOptionValue } from 'views/companies/settings/helpers';

import Category from 'views/companies/settings/Category';
import SubCategory from 'views/companies/settings/SubCategory';
import VisibleSetting from 'views/companies/settings/VisibleSetting';
import InputLabel from 'views/companies/settings/components/InputLabel';
import Select from '@/library/components/form/inputs/select';
import Checkbox from '@/library/components/form/inputs/Checkbox';
import { CATEGORY_IDS } from '../constants';
import { useCategoryDraft } from '../useCategoryDraft';
import { formatBooleanDisplay, formatOptionDisplay } from '../change-tracking';
import { useCompanySettingsDraft } from '../draft-context';

type MGASettingsProps = {
  companySettings: CompanySettings | undefined;
};

const CATEGORY_ID = CATEGORY_IDS.mga;
const CATEGORY_TITLE = 'MGA';

const DEPLETION_OPTIONS = [
  { label: 'FIFO', value: 'fifo' },
  { label: 'NONE', value: 'none' }
];

const LABELS = {
  [mga_inventory_depletion_method]: 'MGA Inventory Depletion Method',
  [mga_disable_warnings_on_reviewed_period]: 'MGA disable seaman wages warnings on reviewed periods'
};

const MGASettings: FC<MGASettingsProps> = ({ companySettings }) => {
  const { formState, loadValues, collectValues } = useForm(config);
  const { selectField, ...rest } = useFormState(formState);
  const fields: FieldsStateType<MGASettingsType> = rest.fields;
  const { isSettingVisible } = useCompanySettingsDraft();

  const initializeForm = useCallback(() => {
    if (!companySettings) return;

    loadValues({
      [mga_inventory_depletion_method]: companySettings.mga_inventory_depletion_method,
      [mga_disable_warnings_on_reviewed_period]:
        companySettings.mga_disable_warnings_on_reviewed_period === '1'
    });
  }, [companySettings, loadValues]);

  useCategoryDraft({
    categoryId: CATEGORY_ID,
    categoryTitle: CATEGORY_TITLE,
    companySettings,
    initializeForm,
    collectValues: (validate = true) => collectValues(validate) as MGASettingsType | null,
    toPayload: values => [
      {
        key: mga_inventory_depletion_method,
        value: getOptionValue(values[mga_inventory_depletion_method])
      },
      {
        key: mga_disable_warnings_on_reviewed_period,
        value: values[mga_disable_warnings_on_reviewed_period] ? '1' : '0'
      }
    ],
    labels: LABELS,
    formatValue: (key, value) =>
      key === mga_disable_warnings_on_reviewed_period
        ? formatBooleanDisplay(value)
        : formatOptionDisplay(value, DEPLETION_OPTIONS),
    watch: fields
  });

  return (
    <Category id={CATEGORY_ID} title={CATEGORY_TITLE}>
      <Row className="d-flex align-items-center gx-1">
        <Col xs={3}>
          <SubCategory
            title="Inventory"
            hidden={!isSettingVisible(CATEGORY_ID, mga_inventory_depletion_method)}
            className="border-bottom border-gray-200"
          >
            <VisibleSetting categoryId={CATEGORY_ID} settingKey={mga_inventory_depletion_method}>
              <Select
                className="mb-0"
                label={
                  <InputLabel
                    title="MGA INVENTORY DEPLETION METHOD"
                    apiKey={mga_inventory_depletion_method}
                  />
                }
                options={DEPLETION_OPTIONS}
                onChange={(option: MGAInventoryDepletionMethod) => {
                  selectField(mga_inventory_depletion_method)(option);
                }}
                placeholder="Select method"
                {...fields[mga_inventory_depletion_method]}
              />
            </VisibleSetting>
          </SubCategory>
        </Col>
        <Col className="d-flex align-items-center align-self-end h-24 ps-1">
          <SubCategory
            className="cpb-2 cmb-2"
            title="Warnings"
            hidden={!isSettingVisible(CATEGORY_ID, mga_disable_warnings_on_reviewed_period)}
          >
            <VisibleSetting
              categoryId={CATEGORY_ID}
              settingKey={mga_disable_warnings_on_reviewed_period}
            >
              <Checkbox
                className="mb-0"
                label={
                  <InputLabel
                    title="MGA disable seaman wages warnings on reviewed periods"
                    apiKey={mga_disable_warnings_on_reviewed_period}
                    isCheckbox
                  />
                }
                labelClassName="fs-12 fw-medium text-primary"
                onChange={() =>
                  selectField(mga_disable_warnings_on_reviewed_period)(
                    !fields[mga_disable_warnings_on_reviewed_period].value
                  )
                }
                {...fields[mga_disable_warnings_on_reviewed_period]}
              />
            </VisibleSetting>
          </SubCategory>
        </Col>
      </Row>
    </Category>
  );
};

export default MGASettings;
